import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { CollectionStatsType, StoryStatusType, StoryType } from '../../../../lib/types'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import PublishIcon from '@mui/icons-material/Publish'
import UnpublishedIcon from '@mui/icons-material/Unpublished'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import Checkbox from '@mui/material/Checkbox'
import { isMobile } from 'react-device-detect'
import { format, parseJSON } from 'date-fns'
import Paper from '@mui/material/Paper'
import DeleteIcon from '@mui/icons-material/Delete'
import CollectionMove from './CollectionMove'
import api from '../../../../lib/api'
import { SimpleSnackbarContext } from '../../../../components/SimpleSnackbar'
import { SimpleDialogContext } from '../../../../components/SimpleDialog'
import MoreIcon from '@mui/icons-material/MoreVert'
import MenuItem from '@mui/material/MenuItem'
import SortControl from './SortControl'
import { SortControlContext } from '../../../../components/SortControlProvider'
import TextField from '@mui/material/TextField'
import { CopyAll, Photo, PictureInPicture, Restore, Upload, TextSnippet, AutoFixHigh } from '@mui/icons-material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Grid from '@mui/material/Grid'
import { cloneStoriesToCollection, recreateThumbs, storyReprocessTranscript } from '../../../../lib/api/services'
import Thumbnails from '../../../../lib/videojs/components/thumbnails'
import EditIcon from '@mui/icons-material/Edit'
import FormControl from '@mui/material/FormControl'
import { Chip, CircularProgress, InputLabel, Select, SelectChangeEvent, Tooltip } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import { SocketConnectorContext } from '../../../../components/SocketConnector'
import debounce from 'lodash/debounce'
import { StoryFilters } from '../storyFilters'

interface iStory {
  story: StoryType
  collectionId: string
  playing: boolean
  canSeletecForEdit: boolean
  selectForEdit: boolean
  handleSelectForEdit: (id: string, isToAdd: boolean) => void
  isSelected: boolean
  canEdit: boolean
  processingStatus?: StoryStatusType
  onProcessTranscript: (story: StoryType) => Promise<void>
  onProcessGraphics: (story: StoryType) => Promise<void>
}

const hasTranscript = (story: StoryType) => !!(story.transcriptReady || story.hasTranscript || story.transcriptionId)

const isStoryPlayable = (story: StoryType) => !!(story.ready && story.url)

const getNotReadyReason = (story: StoryType): string | undefined => {
  if (!story.ready && story.error) {
    return 'Media conversion failed. Reprocess this story to generate optimized media and thumbnail.'
  }
  if (!story.ready) {
    return 'Media conversion has not completed successfully yet.'
  }
  if (!story.url) {
    return 'Playable media URL is missing.'
  }
  if (!story.thumbnailUrl) {
    return 'Thumbnail is missing.'
  }
  return undefined
}

const getTranscriptBadge = (
  story: StoryType,
  status?: StoryStatusType
): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
  if (status?.processingTranscript || story.processingTranscript || story.transcriptStatus === 'PROCESSING') {
    return { label: 'Transcription queued/processing', color: 'warning' }
  }

  if (hasTranscript(story)) {
    return { label: 'Transcribed', color: 'success' }
  }

  if (status?.transcriptFailureReason || story.transcriptStatus === 'ERROR') {
    return { label: 'Transcript Error', color: 'error' }
  }

  return { label: 'No Transcript', color: 'default' }
}

const statusFromStory = (story: StoryType): StoryStatusType => ({
  storyId: story.id,
  processingGraphics: story.processingGraphics ?? false,
  processingTranscript: story.processingTranscript ?? false,
  processingSubtitle: story.processingSubtitle ?? false,
  processingMedia: false,
  mediaReady: isStoryPlayable(story),
  mediaError: story.error ?? false,
  mediaStatus: story.ready ? 'SUCCESS' : story.error ? 'ERROR' : undefined,
  thumbnailReady: story.type !== 'VIDEO' || !!story.thumbnailUrl,
  mergeStatus: story.mergeStatus,
  graphicsProcessed: story.graphicsProcessed,
  sentToDam: story.sentToDam,
  graphicsFailureReason: story.graphicsFailureReason,
  graphicsLastAttempt: story.graphicsLastAttempt,
  graphicsAttemptCount: story.graphicsAttemptCount,
  transcriptFailureReason: story.transcriptFailureReason,
  transcriptLastAttempt: story.transcriptLastAttempt,
  transcriptAttemptCount: story.transcriptAttemptCount
})

const hasActiveProcessing = (status?: StoryStatusType) =>
  !!status &&
  (status.processingGraphics || status.processingTranscript || status.processingSubtitle || status.processingMedia)

const CHECK_PROCESSING_CONCURRENCY = 8

const storyNeedsProcessingCheck = (story: StoryType): boolean => {
  const status = statusFromStory(story)
  if (hasActiveProcessing(status)) {
    return true
  }
  const merge = status.mergeStatus?.toLowerCase() ?? ''
  if (merge.includes('queued') || merge.includes('processing')) {
    return true
  }
  if (!story.ready && !story.error) {
    return true
  }
  return false
}

interface iStoryProcessingControls {
  story: StoryType
  canEdit: boolean
  status?: StoryStatusType
  onProcessTranscript: (story: StoryType) => Promise<void>
  onProcessGraphics: (story: StoryType) => Promise<void>
}

const StoryProcessingControls: React.FC<iStoryProcessingControls> = ({
  story,
  canEdit,
  status,
  onProcessTranscript,
  onProcessGraphics
}) => {
  const [hydrated, setHydrated] = useState(false)
  const currentStatus = status || statusFromStory(story)
  const isMediaProcessing = currentStatus.processingMedia ?? false
  const mediaReady = currentStatus.mediaReady ?? isStoryPlayable(story)
  const mediaError = currentStatus.mediaError ?? story.error
  const transcriptBadge = getTranscriptBadge(story, currentStatus)
  const storyHasTranscript = hasTranscript(story)
  const graphicsQueued = currentStatus.mergeStatus?.toLowerCase()?.includes('queued') ?? false
  const isTranscriptProcessing = currentStatus.processingTranscript || story.transcriptStatus === 'PROCESSING'
  const isGraphicsProcessing = currentStatus.processingGraphics
  const graphicsProcessed = currentStatus.graphicsProcessed ?? story.graphicsProcessed
  const hasGraphicsError = !!currentStatus.graphicsFailureReason || currentStatus.mergeStatus?.includes('Error')
  const transcriptTooltip = storyHasTranscript
    ? undefined
    : currentStatus.transcriptFailureReason || story.transcriptFailureReason || undefined
  const graphicsTooltip =
    currentStatus.graphicsFailureReason || story.graphicsFailureReason || currentStatus.mergeStatus || undefined
  const mediaTooltip = isMediaProcessing
    ? currentStatus.mergeStatus || 'Media queued for processing'
    : getNotReadyReason(story)
  const mediaLabel = isMediaProcessing
    ? currentStatus.mergeStatus?.toLowerCase()?.includes('queued')
      ? 'Media queued'
      : 'Processing Media'
    : mediaReady
    ? 'Ready'
    : mediaError
    ? 'Media Error'
    : 'Not Ready'
  const canProcessTranscript =
    canEdit &&
    !isTranscriptProcessing &&
    !storyHasTranscript &&
    (story.transcriptStatus !== 'PROCESSING' || !!currentStatus.transcriptFailureReason)
  const canProcessGraphics = canEdit && !isGraphicsProcessing && !graphicsProcessed
  const graphicsLabel = isGraphicsProcessing
    ? graphicsQueued
      ? 'Graphics queued'
      : 'Processing Graphics'
    : graphicsProcessed
    ? 'Graphics Applied'
    : hasGraphicsError
    ? 'Graphics Error'
    : 'No Graphics'

  useEffect(() => {
    setHydrated(true)
  }, [])

  if (!hydrated) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
      <Tooltip title={mediaTooltip || ''}>
        <Chip
          label={mediaLabel}
          size="small"
          color={mediaReady ? 'success' : mediaError ? 'error' : isMediaProcessing ? 'warning' : 'default'}
          variant="outlined"
          disabled={isMediaProcessing}
          icon={isMediaProcessing ? <CircularProgress size={12} color="inherit" /> : undefined}
        />
      </Tooltip>
      <Tooltip title={transcriptTooltip || ''}>
        <Chip
          label={transcriptBadge.label}
          size="small"
          color={transcriptBadge.color}
          variant="outlined"
          clickable={canProcessTranscript}
          disabled={isTranscriptProcessing}
          icon={isTranscriptProcessing ? <CircularProgress size={12} color="inherit" /> : undefined}
          onClick={canProcessTranscript ? () => onProcessTranscript(story) : undefined}
        />
      </Tooltip>
      <Tooltip title={graphicsTooltip || ''}>
        <Chip
          label={graphicsLabel}
          size="small"
          color={
            graphicsProcessed ? 'success' : hasGraphicsError ? 'error' : isGraphicsProcessing ? 'warning' : 'default'
          }
          variant="outlined"
          clickable={canProcessGraphics}
          disabled={isGraphicsProcessing}
          icon={isGraphicsProcessing ? <CircularProgress size={12} color="inherit" /> : undefined}
          onClick={canProcessGraphics ? () => onProcessGraphics(story) : undefined}
        />
      </Tooltip>
    </Box>
  )
}

const StoryCard: React.FC<iStory> = ({
  story,
  collectionId,
  playing,
  isSelected,
  canSeletecForEdit,
  handleSelectForEdit,
  selectForEdit,
  canEdit,
  processingStatus,
  onProcessTranscript,
  onProcessGraphics
}) => {
  const history = useHistory()
  const [hover, setHover] = useState<boolean>(isMobile)
  const canPlayStory = isStoryPlayable(story)
  const liveStatus = processingStatus || statusFromStory(story)
  const liveMergeStatus = liveStatus.mergeStatus
  const liveMediaReady = liveStatus.mediaReady ?? isStoryPlayable(story)
  const liveGraphicsProcessed = liveStatus.graphicsProcessed ?? story.graphicsProcessed
  const showMergeStatus = !!liveMergeStatus && !liveMediaReady && !liveGraphicsProcessed

  return (
    <Box sx={{ display: 'flex', width: { sm: 'unset', xs: '100%' }, maxHeight: 200, marginBottom: 1 }} key={story.id}>
      <Box sx={{ position: 'relative', width: { sm: 'unset', xs: '100%' } }}>
        <Box
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          sx={{ position: 'relative', width: { sm: 327, xs: '100%' }, flex: { sm: 'unset', xs: 1 }, height: 200 }}
        >
          <Box
            component="img"
            sx={{
              position: 'absolute',
              backgroundColor: 'action.selected',
              objectFit: 'cover',
              height: '100%',
              width: '100%'
            }}
            src={/\.?(png|jpe?g|gif)$/i.test(story.thumbnailUrl) ? story.thumbnailUrl : '/images/CoverDefault.png'}
            alt={story.title}
          />
          {isSelected && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  backgroundColor: 'action.selected',
                  height: '100%',
                  width: '100%',
                  top: 0
                }}
              />
              <Box
                sx={{
                  cursor: 'default',
                  color: 'common.black',
                  position: 'absolute',
                  backgroundColor: 'primary.main',
                  minWidth: 48,
                  paddingRight: 2,
                  paddingTop: '5px',
                  paddingBottom: '5px',
                  borderRadius: '0px 40px 40px 0px',
                  top: '10%',
                  left: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <PlayArrowIcon color="inherit" /> Playing
              </Box>
            </>
          )}
          {!isSelected && hover && canPlayStory && (
            <Button
              variant="contained"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                padding: '20px 16px'
              }}
              onClick={() => {
                history.push(`/a/collections/${collectionId}?storyId=${story.id}&autoplay=true`)
              }}
            >
              <PlayArrowIcon />
            </Button>
          )}
        </Box>
        {canSeletecForEdit && (
          <Box
            sx={{
              display: { xs: 'block', sm: 'none' },
              position: 'absolute',
              top: '8px',
              right: '8px'
            }}
          >
            <Checkbox
              checked={selectForEdit}
              onChange={e => handleSelectForEdit(story.id, e.target.checked)}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', sm: 'flex' },
          flexFlow: 'row',
          backgroundColor: playing ? 'action.selected' : 'default'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'column',
            width: '100%',
            padding: 3
          }}
        >
          <Box mb="0.35em">
            <Typography sx={{ fontWeight: '700', mb: 'unset' }} variant="h5" gutterBottom>
              {`${story.title}`}
            </Typography>
            <Typography component="div" variant="caption" sx={{ fontWeight: '700' }}>
              Recorder: {`${story.recorderName}, ${story.recorderEmail} `}
            </Typography>
            {story.createdAt && (
              <Typography component="div" variant="caption">{`On ${format(
                parseJSON(story.createdAt),
                'PPPp'
              )}`}</Typography>
            )}
            <Typography component="div" variant="caption" sx={{ color: story.published ? '#6EC069' : 'white' }}>
              Status: {`${story.published ? 'Published' : 'Draft'}`}
            </Typography>
            {showMergeStatus && (
              <Typography component="div" variant="caption" sx={{ color: '#6EC069' }}>
                {`${liveMergeStatus}`}
              </Typography>
            )}
            {story.sentToDam && (
              <Typography component="div" variant="caption" sx={{ color: 'cyan' }}>
                Already sent to DAM
              </Typography>
            )}
            <StoryProcessingControls
              story={story}
              canEdit={canEdit}
              status={processingStatus}
              onProcessTranscript={onProcessTranscript}
              onProcessGraphics={onProcessGraphics}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: '400'
            }}
            className="line-clamp"
            variant="h6"
          >
            {story.description}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginRight: 3
          }}
        >
          {canSeletecForEdit ? (
            <Checkbox
              checked={selectForEdit}
              onChange={e => handleSelectForEdit(story.id, e.target.checked)}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          ) : (
            <IconButton component={Link} to={`/a/stories/${story.id}`} target="_blank" title={`Go to ${story.title}`}>
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  )
}

interface iStories {
  collectionId: string
  stories: StoryType[]
  recorders: string[]
  totalStories: number
  playing: boolean
  canEdit: boolean
  selectedStory: string | undefined
  reload: () => void
  stats?: CollectionStatsType
  storyFilters: StoryFilters
  filteringStories: boolean
  hasMoreStories: boolean
  loadingMoreStories: boolean
  onLoadMoreStories: () => void
  onFiltersChange: (filters: StoryFilters) => void
}

const Stories: React.FC<iStories> = ({
  selectedStory,
  collectionId,
  stories,
  recorders,
  totalStories,
  playing,
  canEdit,
  reload,
  stats,
  storyFilters,
  filteringStories,
  hasMoreStories,
  loadingMoreStories,
  onLoadMoreStories,
  onFiltersChange
}) => {
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const { actions: dialogActions } = useContext(SimpleDialogContext)
  const { value: sortValue } = useContext(SortControlContext)
  const { client: socketClient, connected } = useContext(SocketConnectorContext)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [selectedStories, setSelectedStories] = useState<string[]>([])
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState<null | HTMLElement>(null)
  let [deleteText, setDeleteText] = useState<string>()
  let [deleteTextIsRight, setDeleteTextIsRight] = useState<boolean>()
  const [storiesPublished, setStoriesPublished] = useState(0)
  const [storiesSentToDam, setStoriesSentToDam] = useState(0)
  const [searchInput, setSearchInput] = useState(storyFilters.q ?? '')
  const [collectionToClone, setCollectionToClone] = useState<string>('')
  const [storyProcessingStatuses, setStoryProcessingStatuses] = useState<Record<string, StoryStatusType>>({})
  const storyProcessingStatusesRef = useRef<Record<string, StoryStatusType>>({})
  const polledStoryIdsRef = useRef<Set<string>>(new Set())
  const pendingStatusUpdatesRef = useRef<Record<string, StoryStatusType>>({})
  const flushScheduledRef = useRef<number | null>(null)
  const storyFiltersRef = useRef(storyFilters)
  storyFiltersRef.current = storyFilters

  const deleteTextExpected = 'delete the selected ' + selectedStories.length + ''

  const debouncedSearchChange = useMemo(
    () =>
      debounce((query: string) => {
        onFiltersChange({
          ...storyFiltersRef.current,
          q: query || undefined
        })
      }, 400),
    [onFiltersChange]
  )

  useEffect(() => () => debouncedSearchChange.cancel(), [debouncedSearchChange])

  useEffect(() => {
    setSearchInput(storyFilters.q ?? '')
  }, [collectionId, storyFilters.q])

  useEffect(() => {
    setSelectedStories([])
  }, [collectionId, storyFilters.recorderName, storyFilters.q])

  useEffect(() => {
    polledStoryIdsRef.current = new Set()
    pendingStatusUpdatesRef.current = {}
    if (flushScheduledRef.current != null) {
      cancelAnimationFrame(flushScheduledRef.current)
      flushScheduledRef.current = null
    }
  }, [collectionId, storyFilters.recorderName, storyFilters.q, storyFilters.sortBy, storyFilters.sortDirection])

  useEffect(() => {
    const sortBy = sortValue.field.value === 'title' ? 'title' : 'createdAt'
    const current = storyFiltersRef.current
    if (current.sortBy === sortBy && current.sortDirection === sortValue.sort) return
    onFiltersChange({
      ...current,
      sortBy,
      sortDirection: sortValue.sort
    })
  }, [sortValue.field.value, sortValue.sort, onFiltersChange])

  const filteredStories = useMemo(() => {
    return stories.filter(story => {
      if (storyFilters.recorderName && story.recorderName !== storyFilters.recorderName) {
        return false
      }
      if (storyFilters.q) {
        const query = storyFilters.q.toLowerCase()
        if (!(story.title ?? '').toLowerCase().includes(query)) {
          return false
        }
      }
      return true
    })
  }, [stories, storyFilters.recorderName, storyFilters.q])

  const flushPendingStatusUpdates = useCallback(() => {
    flushScheduledRef.current = null
    const pending = pendingStatusUpdatesRef.current
    if (Object.keys(pending).length === 0) {
      return
    }
    pendingStatusUpdatesRef.current = {}
    setStoryProcessingStatuses({ ...storyProcessingStatusesRef.current })
  }, [])

  const scheduleStatusFlush = useCallback(() => {
    if (flushScheduledRef.current != null) {
      return
    }
    flushScheduledRef.current = requestAnimationFrame(flushPendingStatusUpdates)
  }, [flushPendingStatusUpdates])

  const upsertProcessingStatusesBatch = useCallback((statuses: Record<string, StoryStatusType>) => {
    if (Object.keys(statuses).length === 0) {
      return
    }
    storyProcessingStatusesRef.current = {
      ...storyProcessingStatusesRef.current,
      ...statuses
    }
    setStoryProcessingStatuses({ ...storyProcessingStatusesRef.current })
  }, [])

  const upsertProcessingStatus = useCallback(
    (status: StoryStatusType, reloadOnComplete = false, immediate = false) => {
      const previousStatus = storyProcessingStatusesRef.current[status.storyId]
      storyProcessingStatusesRef.current = {
        ...storyProcessingStatusesRef.current,
        [status.storyId]: status
      }
      pendingStatusUpdatesRef.current[status.storyId] = status

      if (immediate) {
        if (flushScheduledRef.current != null) {
          cancelAnimationFrame(flushScheduledRef.current)
          flushScheduledRef.current = null
        }
        pendingStatusUpdatesRef.current = {}
        setStoryProcessingStatuses({ ...storyProcessingStatusesRef.current })
      } else {
        scheduleStatusFlush()
      }

      if (reloadOnComplete && hasActiveProcessing(previousStatus) && !hasActiveProcessing(status)) {
        reload()
      }
    },
    [reload, scheduleStatusFlush]
  )

  const handleProcessStoryTranscript = useCallback(
    async (targetStory: StoryType) => {
      const previousStatus = storyProcessingStatusesRef.current[targetStory.id] || statusFromStory(targetStory)

      upsertProcessingStatus(
        {
          ...previousStatus,
          storyId: targetStory.id,
          processingTranscript: true,
          transcriptFailureReason: undefined
        },
        false,
        true
      )

      try {
        await api.storyReprocessTranscript(targetStory.id)
        snackActions.open('Transcript processing queued. Please wait while the process completes.')
      } catch (error: any) {
        upsertProcessingStatus(previousStatus, false, true)
        snackActions.open(
          error?.response?.status === 409
            ? 'Transcript is already processing.'
            : 'Error starting transcript processing. Please try again.'
        )
      }
    },
    [snackActions, upsertProcessingStatus]
  )

  const handleProcessStoryGraphics = useCallback(
    async (targetStory: StoryType) => {
      if (!targetStory.questionnaireId) {
        snackActions.open(
          'WARN: You cannot incorporate visuals into stories that were not captured through a questionnaire.'
        )
        return
      }

      const previousStatus = storyProcessingStatusesRef.current[targetStory.id] || statusFromStory(targetStory)
      const hasGraphicsError = !!previousStatus.graphicsFailureReason || previousStatus.mergeStatus?.includes('Error')

      upsertProcessingStatus(
        {
          ...previousStatus,
          storyId: targetStory.id,
          processingGraphics: true,
          graphicsProcessed: false,
          graphicsFailureReason: undefined,
          mergeStatus: 'Queued'
        },
        false,
        true
      )

      try {
        if (hasGraphicsError) {
          await api.retryGraphics(targetStory.id)
        } else {
          await api.applyGraphics(targetStory.id)
        }
        snackActions.open('Graphics processing queued. Please wait while the process completes.')
      } catch (error: any) {
        upsertProcessingStatus(previousStatus, false, true)
        snackActions.open(
          error?.response?.status === 409
            ? 'Graphics already processing.'
            : 'Error starting graphics processing. Please try again.'
        )
      }
    },
    [snackActions, upsertProcessingStatus]
  )

  const handleDeleteTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setDeleteText(value)
    deleteText = value
    setDeleteTextIsRight(deleteText === deleteTextExpected || value === deleteTextExpected)
    deleteTextIsRight = deleteText === deleteTextExpected || value === deleteTextExpected
  }

  const handleCollectionIdToClone = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setCollectionToClone(value)
  }

  useEffect(() => {
    if (!connected) {
      return
    }

    const subscription = socketClient.subscribe('/user/queue/story-processing-status', (message: { body: string }) => {
      try {
        const { payload } = JSON.parse(message.body)
        if (payload?.storyId && stories.some(story => story.id === payload.storyId)) {
          upsertProcessingStatus(payload, true, true)
        }
      } catch (error) {
        console.error('Error parsing story processing status message:', error)
      }
    })

    return () => subscription.unsubscribe()
  }, [connected, socketClient, stories, upsertProcessingStatus])

  useEffect(() => {
    let cancelled = false
    const idleStatuses: Record<string, StoryStatusType> = {}
    const storiesToCheck: StoryType[] = []

    stories.forEach(currentStory => {
      if (polledStoryIdsRef.current.has(currentStory.id)) {
        return
      }
      polledStoryIdsRef.current.add(currentStory.id)

      if (storyNeedsProcessingCheck(currentStory)) {
        storiesToCheck.push(currentStory)
        return
      }

      idleStatuses[currentStory.id] = statusFromStory(currentStory)
    })

    if (Object.keys(idleStatuses).length > 0) {
      upsertProcessingStatusesBatch(idleStatuses)
    }

    if (storiesToCheck.length === 0) {
      return () => {
        cancelled = true
      }
    }

    let running = 0
    let index = 0

    const runNext = () => {
      // eslint-disable-next-line no-unmodified-loop-condition -- `cancelled` is set by the effect cleanup
      while (running < CHECK_PROCESSING_CONCURRENCY && index < storiesToCheck.length && !cancelled) {
        const story = storiesToCheck[index++]
        running++
        api
          .checkProcessing(story.id)
          .then(status => {
            if (!cancelled) {
              upsertProcessingStatus(status)
            }
          })
          .catch(error => {
            console.error('Error fetching story processing status snapshot:', error)
            polledStoryIdsRef.current.delete(story.id)
          })
          .finally(() => {
            running--
            runNext()
          })
      }
    }

    runNext()

    return () => {
      cancelled = true
    }
  }, [stories, upsertProcessingStatus, upsertProcessingStatusesBatch])

  const handleDelete = () => {
    dialogActions.open(
      'Delete forever',
      <>
        <Typography fontWeight="700" fontSize={'2rem'}>
          Are you sure you want to delete permanently all {selectedStories.length} stories?
        </Typography>
        <Typography color={'#b45b41'} fontWeight={200} margin={'1em'} fontSize={'1.4rem'}>
          This action cannot be undone!
        </Typography>
        <br></br>
        <TextField
          color="warning"
          size="medium"
          name="deleteConfirmation"
          fullWidth
          required
          onInput={(e: React.ChangeEvent<HTMLInputElement>): void => handleDeleteTextChange(e)}
          placeholder={`${deleteTextExpected}`}
        />
        <br></br>
        <Typography
          fontWeight={100}
          margin={'1em'}
        >{`Type the expected phrase to confirme deletion: ${deleteTextExpected}`}</Typography>
      </>,
      {
        okText: 'Yes, delete',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            if (!deleteTextIsRight) {
              snackActions.open('Type the correct phrase on modal input to delete permanentely the Story.')
            } else {
              await Promise.all(selectedStories.map(async storyId => await api.deleteStory(storyId)))
              reload()
              snackActions.open('Selected stories was deleted!')
            }
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handlePublish = () => {
    dialogActions.open(
      'Publish stories',
      <>
        <Typography fontWeight="700">
          Are you sure you want to update the status of selected stories from Draft to Published?
        </Typography>
      </>,
      {
        okText: 'Yes, update status',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.updateStoryStatus(storyId, true)))
            reload()
            snackActions.open('Selected stories successfully updated to Published status!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handleThumbnails = () => {
    dialogActions.open(
      'Thumbnails',
      <>
        <Typography fontWeight="700">Are you sure you want to re-create the thumbnails of selected stories?</Typography>
      </>,
      {
        okText: 'Yes, regenerate thumbnails',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.recreateThumbs(storyId)))
            reload()
            snackActions.open('Selected stories successfully sent to thumbnail recreate processing!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handleTranscript = () => {
    dialogActions.open(
      'Transcripts',
      <>
        <Typography fontWeight="700">
          Are you sure you want to reprocess the transcripts of selected stories?
        </Typography>
      </>,
      {
        okText: 'Yes, reprocess transcripts',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.storyReprocessTranscript(storyId)))
            reload()
            snackActions.open('Selected stories successfully sent to transcript reprocessing!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handleSendToDam = () => {
    dialogActions.open(
      'Export to DAM',
      <>
        <Typography fontWeight="700">Are you sure you want to export the selected stories to DAM?</Typography>
        <Typography fontWeight="700" fontSize={'small'}>
          Only stories with status published will be send.
        </Typography>
      </>,
      {
        okText: 'Yes, publish and send to DAM',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.sendStoryToDam(storyId)))
            reload()
            snackActions.open('Selected stories successfully published and queued to send to DAM!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handleApplyGraphics = () => {
    dialogActions.open(
      'Apply Graphics',
      <>
        <Typography fontWeight="700">Are you certain you wish to apply graphics to all selected stories?</Typography>
        {/* <Typography fontWeight="500" fontSize={'small'}> */}
        {/*  These functions apply to all stories within the Collection, not just the selected ones. Only stories without */}
        {/*  graphics will undergo the process. Please note that this process will be queued and may take some time to be */}
        {/*  visually ready. */}
        {/* </Typography> */}
      </>,
      {
        okText: 'Yes, apply graphics',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.applyGraphics(storyId)))
            reload()
            snackActions.open('Selected stories successfully queued to applying graphics!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
      // async value => {
      //   if (value) {
      //     try {
      //       await api.applyGraphicsCollection(collectionId)
      //       reload()
      //       snackActions.open('All stories is successfully send to graphics applying!')
      //     } catch (error) {
      //       snackActions.open('Something was wrong! please try again.')
      //     }
      //   }
      // }
    )
  }

  const handleRestoreMedia = () => {
    dialogActions.open(
      'Reprocess media',
      <>
        <Typography fontWeight="700">Are you sure you want to reprocess selected media?.</Typography>
        <Typography fontWeight="500" fontSize={'small'}>
          Reprocessing will start from the original recording, including: Graphics (Intro/Outro/Lower Thirds), the
          Transcript (all changes will be overwritten), and Captions, which will be updated based on the new transcript.
        </Typography>
        {/*  These functions apply to all stories within the Collection, not just the selected ones. They will revert all */}
        {/*  media to their original state before applying graphics. Furthermore, transcripts and captions will be queued */}
        {/*  for reprocessing. */}
        {/* </Typography> */}
      </>,
      {
        okText: 'Yes, reprocess media(s)!',
        showOk: true,
        cancelText: 'No, keep as is!'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.restoreMedia(storyId)))
            reload()
            snackActions.open('Selected stories successfully queued to reprocess media!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
      // async value => {
      //   if (value) {
      //     try {
      //       await api.restoreMediaCollection(collectionId)
      //       reload()
      //       snackActions.open('All stories is successfully send to restoring process!')
      //     } catch (error) {
      //       snackActions.open('Something was wrong! please try again.')
      //     }
      //   }
      // }
    )
  }

  const handleDraft = () => {
    dialogActions.open(
      'Set status to draft',
      <>
        <Typography fontWeight="700">
          Are you sure you want to update the status of selected stories from Published to Draft?
        </Typography>
      </>,
      {
        okText: 'Yes, update status',
        showOk: true,
        cancelText: 'No, keep as is'
      },
      async value => {
        if (value) {
          try {
            await Promise.all(selectedStories.map(async storyId => await api.updateStoryStatus(storyId, false)))
            reload()
            snackActions.open('Selected stories successfully updated to Draft status')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handleCancel = () => {
    setEditMode(false)
    setSelectedStories([])
  }

  const handleSelectAll = () => {
    !selectedStories.length ? setSelectedStories(() => filteredStories.map(item => item.id)) : setSelectedStories([])
  }

  const handleCloneStories = () => {
    dialogActions.open(
      'Clone Stories to new Collection',
      <>
        <Typography fontWeight="700">Enter the collection ID to clone the selected stories</Typography>
        <br />
        <TextField
          color="warning"
          size="medium"
          name="collectionIdToClone"
          fullWidth
          required
          onInput={(e: React.ChangeEvent<HTMLInputElement>): void => handleCollectionIdToClone(e)}
          placeholder={`Collection ID`}
        />
        <br />
        <Typography variant={'body2'}>Collection: {collectionToClone}</Typography>
      </>,
      {
        okText: 'Clone stories',
        showOk: true,
        cancelText: 'Cancel'
      },
      async value => {
        if (value) {
          try {
            if (!collectionToClone) {
              alert('Invalid or blank Collection ID')
              snackActions.open('No collection informed. - Canceling the clone action.')
              return
            }
            await Promise.all(
              selectedStories.map(async storyId => await api.cloneStoriesToCollection(storyId, collectionToClone))
            )
            snackActions.open('Selected stories successfully cloned to informed collection!')
          } catch (error) {
            snackActions.open('Something was wrong! please try again.')
          }
        }
      }
    )
  }

  const handleProcessMissingTranscripts = () => {
    dialogActions.open(
      'Reprocess Missing Transcripts',
      <>
        <Typography fontWeight="700">
          Process transcripts for all stories in this collection that are missing a successful transcript?
        </Typography>
        <Typography fontWeight="500" fontSize={'small'}>
          This checks the full collection in the database, not only the stories loaded on this page. Only stories with
          optimized media and without a completed transcript will be processed.
        </Typography>
      </>,
      {
        okText: 'Yes, process missing transcripts',
        showOk: true,
        cancelText: 'Cancel'
      },
      async value => {
        if (value) {
          try {
            const result = await api.processMissingTranscripts(collectionId)
            snackActions.open(
              `Transcripts: ${result.started} started, ${result.skipped} skipped, ${result.failed} failed (of ${result.eligible} eligible)`
            )
            reload()
          } catch (error) {
            snackActions.open('Something went wrong! Please try again.')
          }
        }
      }
    )
  }

  const handleProcessMissingGraphics = () => {
    dialogActions.open(
      'Apply Missing Graphics',
      <>
        <Typography fontWeight="700">
          Apply graphics to all stories in this collection that do not yet have graphics applied?
        </Typography>
        <Typography fontWeight="500" fontSize={'small'}>
          This checks the full collection in the database, not only the stories loaded on this page. Only stories with
          optimized video, a configured questionnaire, and no applied graphics will be processed.
        </Typography>
      </>,
      {
        okText: 'Yes, apply missing graphics',
        showOk: true,
        cancelText: 'Cancel'
      },
      async value => {
        if (value) {
          try {
            const result = await api.processMissingGraphics(collectionId)
            snackActions.open(
              `Graphics: ${result.started} started, ${result.skipped} skipped, ${result.failed} failed (of ${result.eligible} eligible)`
            )
            reload()
          } catch (error) {
            snackActions.open('Something went wrong! Please try again.')
          }
        }
      }
    )
  }

  const handleProcessNotReadyStories = () => {
    dialogActions.open(
      'Process Stories not Ready',
      <>
        <Typography fontWeight="700">Reprocess stories in this collection that are not ready for playback?</Typography>
        <Typography fontWeight="500" fontSize={'small'}>
          This checks the full collection in the database and queues eligible stories in background batches. A Story Not
          Ready does not yet have playable optimized media, a successful media conversion, or a required thumbnail.
        </Typography>
      </>,
      {
        okText: 'Yes, process not ready stories',
        showOk: true,
        cancelText: 'Cancel'
      },
      async value => {
        if (value) {
          try {
            const result = await api.processNotReadyStories(collectionId)
            snackActions.open(
              `Not ready stories: ${result.queued ?? result.started} queued for background processing (of ${
                result.eligible
              } eligible)`
            )
            reload()
          } catch (error) {
            snackActions.open('Something went wrong! Please try again.')
          }
        }
      }
    )
  }

  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl)

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget)
  }

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null)
  }

  useEffect(() => {
    let publishedCount = 0
    let sentToDam = 0

    stories.forEach(story => {
      if (story.published) publishedCount += 1
      if (story.sentToDam) sentToDam += 1
    })

    setStoriesPublished(publishedCount)
    setStoriesSentToDam(sentToDam)
  }, [stories])

  const handleSelectionChange = (event: SelectChangeEvent<string>) => {
    const recorderName = event.target.value || undefined
    onFiltersChange({
      ...storyFiltersRef.current,
      recorderName
    })
  }

  const handleFilterInput = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value
    setSearchInput(query)
    debouncedSearchChange(query)
  }

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <CollectionMove
        isMenu
        disabled={!selectedStories.length}
        reload={reload}
        stories={selectedStories}
        currentCollectionId={collectionId}
      />
      <MenuItem
        onClick={() => {
          handlePublish()
          handleMobileMenuClose()
        }}
      >
        Publish
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleDraft()
          handleMobileMenuClose()
        }}
      >
        Unpublish
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleDelete()
          handleMobileMenuClose()
        }}
      >
        Delete
      </MenuItem>
    </Menu>
  )

  return (
    <>
      <Box width="90%">
        <Box width="90%">
          <Grid container spacing={2} style={{ width: '100%', margin: '0 auto', flexWrap: 'nowrap' }}>
            {/* Adiciona um espaçamento entre as colunas */}
            <Grid item xs={2}>
              <Paper
                style={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <Typography variant="body2">Total: {totalStories}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Published: {storiesPublished}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <Paper
                style={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <Typography variant="body2">Ready: {stats?.ready ?? stats?.optimized ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Transcribed: {stats?.transcribed ?? '—'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <Paper
                style={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <Typography variant="body2">Graphics: {stats?.graphicsApplied ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Sent to DAM: {storiesSentToDam}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <Paper
                style={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <Typography variant="body2" color={stats?.errors ? 'error' : 'text.primary'}>
                  Errors: {stats?.errors ?? 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <Paper
                style={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FormControl fullWidth>
                  <InputLabel id="recorder-select-label">Filter by recorder</InputLabel>
                  <Select
                    labelId="recorder-select-label"
                    value={storyFilters.recorderName ?? ''}
                    label="Filter by recorder"
                    onChange={handleSelectionChange}
                    disabled={filteringStories}
                  >
                    <MenuItem value="">
                      <em>Select a recorder to filter</em>
                    </MenuItem>
                    {recorders.map((recorder, index) => (
                      <MenuItem key={index} value={recorder}>
                        {recorder}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
            <Grid item xs={2}>
              <Paper
                style={{
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FormControl fullWidth>
                  <TextField
                    size="medium"
                    name="search-text"
                    fullWidth
                    value={searchInput}
                    onChange={handleFilterInput}
                    placeholder={'Filter by text'}
                    disabled={filteringStories}
                  />
                </FormControl>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          padding: '0 24px',
          marginBottom: 3,
          height: 65,
          display: 'flex',
          alignItems: 'center'
        }}
        component={editMode ? Paper : 'div'}
      >
        {editMode ? (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Box>
                <Button onClick={handleSelectAll} color="secondary" startIcon={<SelectAllIcon />}>
                  {!selectedStories.length ? 'Select loaded stories' : `Deselect all (${selectedStories.length})`}
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  disabled={!selectedStories.length}
                  onClick={handleApplyGraphics}
                  color="secondary"
                  startIcon={<PictureInPicture />}
                >
                  Apply graphics
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  disabled={!selectedStories.length}
                  onClick={handleRestoreMedia}
                  color="secondary"
                  startIcon={<Restore />}
                >
                  Reprocess media
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  onClick={handleThumbnails}
                  disabled={!selectedStories.length}
                  color="secondary"
                  startIcon={<Photo />}
                >
                  Recreate Thumbnails
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  onClick={handleTranscript}
                  disabled={!selectedStories.length}
                  color="secondary"
                  startIcon={<EditIcon />}
                >
                  Reprocess Transcripts
                </Button>
              </Box>
              {/* <Box sx={{ display: { xs: 'none', md: 'flex' } }}> */}
              {/*  <Button */}
              {/*    onClick={handleCloneStories} */}
              {/*    disabled={!selectedStories.length} */}
              {/*    color="secondary" */}
              {/*    startIcon={<CopyAll />} */}
              {/*  > */}
              {/*    Clone stories */}
              {/*  </Button> */}
              {/* </Box> */}
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <CollectionMove
                  disabled={!selectedStories.length}
                  reload={reload}
                  stories={selectedStories}
                  currentCollectionId={collectionId}
                />
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  onClick={handlePublish}
                  disabled={!selectedStories.length}
                  color="secondary"
                  startIcon={<PublishIcon />}
                >
                  Publish
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  onClick={handleDraft}
                  disabled={!selectedStories.length}
                  color="secondary"
                  startIcon={<UnpublishedIcon />}
                >
                  Unpublish
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  onClick={handleSendToDam}
                  disabled={!selectedStories.length}
                  color="secondary"
                  startIcon={<UploadFileIcon />}
                >
                  Export to DAM
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Button
                  onClick={handleDelete}
                  disabled={!selectedStories.length}
                  color="secondary"
                  startIcon={<DeleteIcon />}
                >
                  Delete
                </Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1 }} />
              <Box display="flex" alignItems="center">
                <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 2 }}>
                  <IconButton size="large" onClick={handleMobileMenuOpen} color="inherit">
                    <MoreIcon />
                  </IconButton>
                </Box>
                <div>
                  <Button onClick={handleCancel} color="secondary" variant="outlined">
                    Cancel
                  </Button>
                </div>
              </Box>
            </Stack>
            {renderMobileMenu}
          </>
        ) : (
          <>
            <Box>
              <SortControl />
            </Box>
          </>
        )}
        <div style={{ flex: 1 }} />
        {canEdit && !editMode && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              onClick={handleProcessMissingTranscripts}
              color="secondary"
              variant="outlined"
              size="small"
              startIcon={<TextSnippet />}
            >
              Reprocess Missing Transcripts
            </Button>
            <Button
              onClick={handleProcessMissingGraphics}
              color="secondary"
              variant="outlined"
              size="small"
              startIcon={<AutoFixHigh />}
            >
              Apply Missing Graphics
            </Button>
            <Tooltip title="A Story Not Ready has no playable optimized media, successful media conversion, or required thumbnail yet. This will only queue those stories for media processing.">
              <Button
                onClick={handleProcessNotReadyStories}
                color="secondary"
                variant="outlined"
                size="small"
                startIcon={<Restore />}
              >
                Process Stories not Ready
              </Button>
            </Tooltip>
            <Button onClick={() => setEditMode(true)} color="secondary" variant="outlined">
              Select stories
            </Button>
          </Stack>
        )}
      </Box>
      <Box component={Paper} sx={{ position: 'relative' }}>
        {filteringStories && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              zIndex: 1
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        <Box sx={{ display: 'flex', flexFlow: 'column', height: '100%' }}>
          {filteredStories.map(story => (
            <StoryCard
              collectionId={collectionId}
              key={story.id}
              story={story}
              isSelected={story.id === selectedStory}
              playing={playing && story.id === selectedStory}
              canSeletecForEdit={editMode}
              selectForEdit={selectedStories.some(item => item === story.id)}
              canEdit={canEdit}
              processingStatus={storyProcessingStatuses[story.id]}
              onProcessTranscript={handleProcessStoryTranscript}
              onProcessGraphics={handleProcessStoryGraphics}
              handleSelectForEdit={(id, isToAdd) => {
                if (isToAdd) {
                  setSelectedStories([...selectedStories, id])
                  return
                }
                setSelectedStories(selectedStories.filter(item => item !== id))
              }}
            />
          ))}
          {hasMoreStories && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Button onClick={onLoadMoreStories} disabled={loadingMoreStories} color="secondary" variant="outlined">
                {loadingMoreStories ? 'Loading stories...' : `Load more stories (${stories.length}/${totalStories})`}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}

export default Stories
